import React from "react";
import { useTranslation } from 'react-i18next';
import "../../styles/vesselstability.css";
import { lighten } from '@mui/material/styles';
import { 
  Box,
  Grid, 
  Paper,
  Typography,
} from "@mui/material";
import RenderStabilityTable from "../VesselStabilityTable";
import { getDepartureStabilityState } from "../utils/vesselState";
import { containerGroupedByAxis, getHighestDraughtForWaterType, sideWeights } from "../utils/vesselWeightHelpers";
import WeightOverBeam from "../settings/vessel/WeightOverBeam";
import WeightOverLength from "../settings/vessel/WeightOverLength";

const WEIGHT_X_AXIS_TYPE = "x_axis";
const WEIGHT_Y_AXIS_TYPE = "y_axis";

const VesselLoadStability = ({ vessel, containers, terminalCall }) => {
  const { t } = useTranslation();

  const containerGroupedByYAxis = containerGroupedByAxis(
    vessel,
    containers,
    terminalCall,
    WEIGHT_Y_AXIS_TYPE
  );

  const containerGroupedByXAxis = containerGroupedByAxis(
    vessel,
    containers,
    terminalCall,
    WEIGHT_X_AXIS_TYPE
  );

  if (
    containerGroupedByYAxis.length === 0 ||
    containerGroupedByXAxis.length === 0
  ) {
    return "";
  }

  const stabilityState = getDepartureStabilityState(vessel, terminalCall);
  const stability = stabilityState?.stability;

  const { z: loaded = 0 } = stability?.cog_loaded ?? {};
  const { z: maxLoaded = 0 } = stability?.cog_loaded_max ?? {};

  const isOverLoad = loaded > maxLoaded && maxLoaded !== 1 && loaded !== 1;

  const tonnage = stabilityState?.statistics.total_weight

  const maxDraught = getHighestDraughtForWaterType(
    'fresh',
    vessel?.totalWeightDraughtTable
  );

  const isDraughtOver =  stabilityState?.statistics?.draught === 0 || (maxDraught && stabilityState?.statistics?.draught > maxDraught);
  const isTotalWeightOver = tonnage === 0 || tonnage > vessel?.maxWeight;

  return (
    <>
      <Box component={Paper} border={`1px solid ${ lighten("#343A40", 0.7)}`}>
        <Typography 
          variant="subtitle1" 
          className={`vessel-stability-title${ isOverLoad ? '--overload' : '--stable'}`}
          borderRadius="2px 2px 0 0"
        >
          {t("stability")}
        </Typography>
        <Grid 
          item 
          padding={1}
          backgroundColor={ isOverLoad ? lighten('#DC3545', 0.9) : lighten('#28A745', 0.9) }
        >
          { stability?.cog_loaded_max["z"] !== 1 ? (
            <Grid item>
              {`${t("maxStabilityKG")}: ${maxLoaded}`}
            </Grid>
          ) : ""}
          <Grid item display="flex" flexDirection="row">
            <Typography>
              {`${t("stabilityKG")}:`}
            </Typography>
            <Typography className={ `vessel-stability${isOverLoad ? "--overload": ""}`}>
              { loaded }
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Grid item mt={2} mb={2}> 
        <RenderStabilityTable vessel={vessel} containers={containers} cogMaxLoad={stability?.cog_loaded_max["z"]} cogLoaded={loaded} />
      </Grid>
      <WeightOverLength
        vessel={vessel}
        containerWeights={containerGroupedByXAxis}
        tonnage={stabilityState?.statistics?.total_weight}
      />
      <WeightOverBeam
        vessel={vessel}
        containerWeights={containerGroupedByYAxis}
        containers={containers}
        terminalCall={terminalCall}
      />
      <Box component={Paper} border={`1px solid ${ lighten("#343A40", 0.7)}`} mb={2}>
        <Grid 
          item 
          display="flex"
          flexDirection="column"
          gap="5px"
        >
          <Grid item>
            <Typography variant="subtitle1" className="vessel-stability-title">
              {`${t("vesselStateOnDeparture")}`}
            </Typography>
            <Grid item padding={1}>
              <Grid item>
                {`${t("totalPlannedContainerWeight")}: ${containers.reduce(
                  (acc, container) => (acc += container.weight),
                  0
                )/1000}T`}
              </Grid>
              <Grid item>
                {`${t("totalDraught")}: ${stabilityState?.statistics?.draught}cm`}
              </Grid>
              <Grid item>
                {`${t("totalWeightVessels")}: ${stabilityState?.statistics?.total_weight.toFixed(0)}T`}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Box component={Paper} border={`1px solid ${ lighten("#343A40", 0.7)}`}>
        <Grid container gap={1} alignItems="flex-start" flexWrap="nowrap" backgroundColor={lighten("#007BFF", 0.9)}>
          <Grid  container flexDirection="column" justifyContent="center"  alignItems="center">
              <Typography fontWeight={700}>{ vessel.emptyWeightInTonnes ?? 1000.0 }</Typography>
              <Typography variant="subtitle2">({t("emptyWeight")})</Typography>
          </Grid>
          <Typography>+</Typography>
          <Grid  container flexDirection="column" justifyContent="center" alignItems="center">
              <Typography fontWeight={700} sx={{
                color: isDraughtOver ? '#ca4141' : "inherit",
              }}>{
                sideWeights(containerGroupedByYAxis).starboard +
                sideWeights(containerGroupedByYAxis).port
              }</Typography>
              <Typography variant="subtitle2">({t("totalWeight")})</Typography>
          </Grid>
          <Typography>=</Typography>
          <Grid  container flexDirection="column" justifyContent="center" alignItems="center">
              <Typography fontWeight={700}>{
                sideWeights(containerGroupedByYAxis).starboard +
                sideWeights(containerGroupedByYAxis).port +
                (vessel.emptyWeightInTonnes ?? 1000.0)
              }T</Typography>
              <Typography variant="subtitle2" sx={{
                color: isDraughtOver ? '#ca4141' : "inherit",
                fontWeight: isDraughtOver ? 700 : "inherit",
              }}>{stabilityState?.statistics?.draught}cm {t("draught").toLowerCase()}</Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default VesselLoadStability;
